import { combineReducers } from 'redux'

import { AccountType, User }    from '../../api'
import { api, getProcessUsers } from '../../client'
import { bind, remove, unique } from '../../utils'
import createAction             from '../redux-utils/create-action'
import { Reducer }              from '../types'
import { createRequest }        from './communication.duck'
import { ApplicationState }     from './index'

const NAMESPACE = 'users'

export const USERS_GET_SUCCESS = `[${NAMESPACE}] GET_SUCCESS`
export const USERS_DELETED = `[${NAMESPACE}] DELETED`
export const USERS_INVITED = `[${NAMESPACE}] INVITED`

interface ByIdState {
  [ id: number ]: User
}

const byId: Reducer<ByIdState> = (state = {}, action) => {
  const { type, payload } = action

  switch (type) {
    case USERS_INVITED: {
      const { id, type, email } = payload

      const user: User = {
        id,
        account: {
          email,
          type,
          active: false,
        },
      } as any

      /*
       data-model has all user-properties as mandatory
       but that's not the case for inactive users
       */

      return {
        ...state,
        [ id ]: user,
      }
    }

    case USERS_GET_SUCCESS: {
      const users = payload.users as User []

      return users.reduce(
        (reduction, user) => ({
          ...reduction,
          [ user.id ]: user,
        }),
        { ...state },
      )
    }

    case USERS_DELETED: {
      const { [ payload.id ]: user, ...rest } = state

      return rest
    }

    default:
      return state
  }
}

type IdsState = number []
const ids: Reducer<IdsState> = (state = [], action) => {
  const { type, payload } = action

  switch (type) {
    case USERS_INVITED:
      return [ ...state, payload.id ]

    case USERS_GET_SUCCESS: {
      const ids = payload.users.map(({ id }) => id)

      return unique([ ...ids, ...state ])
    }

    case USERS_DELETED:
      return remove(state, payload.id)

    default:
      return state
  }
}

export interface PeopleState {
  byId: ByIdState
  ids: IdsState
}

export default combineReducers({
  byId,
  ids,
})

const bindApi = bind(api)

const userGetSuccess = response =>
  createAction(
    USERS_GET_SUCCESS,
    {
      users: [ response ],
    },
  )

const usersGetSuccess = response =>
  createAction(
    USERS_GET_SUCCESS,
    {
      users: response.results,
    },
  )

const processUsersGetSuccess = response =>
  createAction(
    USERS_GET_SUCCESS,
    {
      users: response,
    },
  )

const inviteUserSuccess = (
  { id },
  user,
) => createAction(
  USERS_INVITED,
  { ...user, id },
  {
    toast: {
      message: 'successfullyInvited',
      values:  { email: user.email },
    },
  },
)

const genHandleUserDeleted = id =>
  createAction(
    USERS_DELETED,
    { id },
    {
      toast: {
        message: 'userDeleted',
      },
    }
  )

export const userGetRequest = createRequest({
  namespace:      `${NAMESPACE}-by-id-get`,
  request:        bindApi(api.getUser),
  successHandler: userGetSuccess,
})

export const usersRequest = createRequest({
  namespace:      `${NAMESPACE}-get`,
  request:        bind(api)(api.getUsers),
  successHandler: usersGetSuccess,
})

export const processUsersGetRequest = createRequest({
  namespace:      `${NAMESPACE}-for-process-get`,
  request:        getProcessUsers,
  successHandler: processUsersGetSuccess,
})

export const inviteUserRequest = createRequest({
  request:        bindApi(api.inviteUser),
  namespace:      `${NAMESPACE}-invite`,
  successHandler: inviteUserSuccess,
})

export const genUsersDeleteRequest = (id: number) => createRequest({
  request:        bindApi(() => api.deleteUser(id)),
  namespace:      `${NAMESPACE}-delete-id-${id}`,
  successHandler: genHandleUserDeleted(id),
})

/* SELECTORS */

export const selectUserById = (id: number) =>
  (state: ApplicationState) =>
    state.users.byId[ id ]

/* this sorts by id and therefore by moment added by default */
export const selectAllUsers =
  ({ users: { ids, byId } }: ApplicationState) =>
    ids
      .sort((a, b) => b - a)
      .map(id => byId[ id ])

export const genSelectByType =
  (type: AccountType) =>
    (state: ApplicationState) =>
      selectAllUsers(state)
        .filter(user => user.account.type === type)

export const selectUsersByProcessId = processId => state => {
  const process = state.processes.byId[ processId ]

  if (!process) {
    return {}
  }

  const users = selectAllUsers(state)

  let student
  let teacher

  if (process.student && process.teacher) {
    student = users.find(({ id }) => id === process.student.id)
    teacher = users.find(({ id }) => id === process.teacher.id)
  }

  return {
    student,
    teacher,
  }
}

export const selectMentors = genSelectByType(AccountType.Mentor)
export const selectMentees = genSelectByType(AccountType.Mentee)
export const getCoaches = genSelectByType(AccountType.Coach)
export const getCoachees = genSelectByType(AccountType.Coachee)



// WEBPACK FOOTER //
// ./src/redux/ducks/users.duck.ts